/* Auto gift-with-purchase engine — adds/removes configured gift products as the
   qualifying cart subtotal crosses each tier's threshold. Gift lines are tagged
   with a _gift property (hidden at checkout) so the theme can suppress qty/remove.

   v3 (17 Sep 2026) — speed. The theme already broadcasts the full cart on every change
   (cart:change → event.detail.cart), so the engine now reconciles against that instantly
   instead of waiting 350ms and re-fetching /cart.js. Gift add/remove requests ask Shopify for
   the rendered drawer in the SAME call (sections param) and hand the result back to the theme
   via cart:change, so there is no separate drawer re-render request either. */
(function () {
  var reconciling = false, pending = false, timer = null;
  var quietUntil = 0;   // suppress fetch-based fallbacks right after an event-driven run
  // Theme cart changes are followed by a cart:change event ~300–800ms later (the theme re-reads
  // /cart.js first). Give that event time to land before a backstop decides nobody handled it.
  var FALLBACK_DELAY = 1000;

  /* The config is read from the page-load HTML and cached. A drawer re-render must never be
     able to blank it: rendering this static section as ?section_id=cart-drawer (without its
     overlay-group prefix) loses the saved settings, emits "[]", and silently switched the whole
     engine off — which is why a gift, once added, was never removed (17 Sep 2026). */
  var cachedTiers = null;
  function tiers() {
    var el = document.getElementById('cart-gift-config');
    var parsed = null;
    if (el) { try { parsed = JSON.parse(el.textContent); } catch (e) {} }
    if (cachedTiers === null) cachedTiers = Array.isArray(parsed) ? parsed : [];
    else if (Array.isArray(parsed) && parsed.length) cachedTiers = parsed;
    return cachedTiers;
  }

  // Group-qualified id, e.g. "sections--20955403550899__cart-drawer" — the bare "cart-drawer"
  // renders with default (empty) settings.
  function drawerSectionId() {
    var wrap = document.querySelector('[id^="shopify-section-"][id$="__cart-drawer"]');
    return wrap ? wrap.id.replace('shopify-section-', '') : 'cart-drawer';
  }

  function isGift(item) {
    return item.properties && (item.properties._gift || item.properties['_gift']);
  }

  function getCart() {
    return fetch('/cart.js', { headers: { 'Accept': 'application/json' } }).then(function (r) { return r.json(); });
  }

  // Every mutation asks for the drawer HTML in the same round trip.
  function mutate(url, body) {
    body.sections = drawerSectionId();
    body.sections_url = window.location.pathname;
    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify(body)
    }).then(function (r) { return r.json(); });
  }

  // Hand the updated cart to the theme exactly the way its own product form does. "variant:add"
  // makes the drawer swap immediately (any other baseEvent waits 1250ms for an animation).
  function publish(cart) {
    var sid = drawerSectionId();
    if (!cart || !cart.sections || !cart.sections[sid]) return fallbackRefresh();
    document.documentElement.dispatchEvent(new CustomEvent('cart:change', {
      bubbles: true,
      detail: { baseEvent: 'variant:add', cart: cart, ssGiftSync: true }
    }));
  }

  function fallbackRefresh() {
    return fetch('/?section_id=' + encodeURIComponent(drawerSectionId()), { headers: { 'Accept': 'text/html' } })
      .then(function (r) { return r.text(); })
      .then(function (html) {
        var cur = document.querySelector('#cart-drawer, cart-drawer');
        if (!cur) return;
        var fresh = new DOMParser().parseFromString(html, 'text/html').querySelector('#cart-drawer, cart-drawer');
        if (fresh) cur.innerHTML = fresh.innerHTML;
      }).catch(function () {});
  }

  /* Plan every change against one cart snapshot, without touching the network.

     Bundle Builder gifts are added by the builder, not by the config above, so tiers() never
     sees them — but they still need policing. Order #52519 (10 Sep 2026) shipped TWO free
     Stubby Coolers on a $189.98 order that earned no discount at all: the shopper built over
     the threshold more than once, then stripped the expensive items back out and kept both
     gifts. Rule: a Bundle Builder gift survives only while at least one NON-gift line still
     carries the same _bundle_id. */
  function plan(cart, cfg) {
    var ops = [];
    var liveBundles = {};
    cart.items.forEach(function (it) {
      var p = it.properties || {};
      if (p._bundle_id && !isGift(it)) liveBundles[p._bundle_id] = true;
    });
    cart.items.forEach(function (it) {
      var p = it.properties || {};
      if (isGift(it) && p._source === 'Bundle Builder' && p._bundle_id && !liveBundles[p._bundle_id]) {
        ops.push({ type: 'change', key: it.key, quantity: 0 });
      }
    });

    // Qualifying subtotal excludes any gift lines (a gift can't unlock itself).
    var sub = 0;
    cart.items.forEach(function (it) { if (!isGift(it)) sub += it.final_line_price; });

    // Strip any manually-added copy of a gift product — only the auto _gift line is allowed.
    var giftVariants = cfg.map(function (g) { return g.variant; });
    cart.items.forEach(function (it) {
      if (giftVariants.indexOf(it.variant_id) !== -1 && !isGift(it)) {
        ops.push({ type: 'change', key: it.key, quantity: 0 });
      }
    });

    cfg.forEach(function (g) {
      if (!g.variant) return;
      var line = cart.items.find(function (it) { return it.variant_id === g.variant && isGift(it); });
      var qualifies = sub >= g.threshold;
      if (qualifies && !line) ops.push({ type: 'add', variant: g.variant });
      else if (!qualifies && line) ops.push({ type: 'change', key: line.key, quantity: 0 });
      else if (qualifies && line && line.quantity !== 1) ops.push({ type: 'change', key: line.key, quantity: 1 });
    });
    return ops;
  }

  /* The theme delays its own drawer swap by 1250ms for anything that isn't "variant:add"
     (e.g. Remove / quantity change). If we publish sooner, that delayed swap lands afterwards
     with the pre-gift-change snapshot and silently puts the removed gift back on screen.
     So after a non-add theme event, hold our publish until the theme's swap has happened. */
  var THEME_SWAP_DELAY = 1250;
  function waitForThemeSwap(meta) {
    if (!meta || !meta.baseEvent || meta.baseEvent === 'variant:add') return Promise.resolve();
    var wait = meta.at + THEME_SWAP_DELAY + 80 - Date.now();
    return wait > 0 ? new Promise(function (r) { setTimeout(r, wait); }) : Promise.resolve();
  }

  async function reconcile(cart, meta) {
    if (reconciling) { pending = true; return; }   // don't drop a change that lands mid-run
    reconciling = true;
    try {
      var cfg = tiers();
      if (!cart || !cart.items) cart = await getCart();
      var ops = plan(cart, cfg);
      if (ops.length) {
        var result = null, lastAdd = null;
        for (var i = 0; i < ops.length; i++) {
          var op = ops[i];
          if (op.type === 'add') {
            lastAdd = await mutate('/cart/add.js', { items: [{ id: op.variant, quantity: 1, properties: { _gift: 'true' } }] });
            result = null;
          } else {
            result = await mutate('/cart/change.js', { id: op.key, quantity: op.quantity });   // full cart + sections
            lastAdd = null;
          }
        }
        var finalCart = result;
        if (!finalCart && lastAdd) {
          // Common case — one $0 gift added: build the cart locally instead of another round trip.
          var added = (lastAdd.items || [])[0];
          if (ops.length === 1 && added && added.final_line_price === 0) {
            finalCart = JSON.parse(JSON.stringify(cart));
            finalCart.items.unshift(added);
            finalCart.item_count += added.quantity;
          } else {
            finalCart = await getCart();
          }
          finalCart.sections = lastAdd.sections;
        }
        await waitForThemeSwap(meta);
        quietUntil = Date.now() + 1500;
        publish(finalCart);
      }
    } catch (e) { /* fail quiet */ }
    reconciling = false;
    if (pending) { pending = false; schedule(); }  // re-read the real cart after a race
  }

  // Fetch-based path, for changes that didn't come with a cart attached.
  function schedule() { clearTimeout(timer); timer = setTimeout(function () { reconcile(null, null); }, 150); }
  function scheduleFallback() { if (Date.now() >= quietUntil && !reconciling) schedule(); }

  /* ── Triggers ─────────────────────────────────────────────────────────────── */

  // 1. Theme cart events carry the whole cart — reconcile immediately, no fetch, no wait.
  document.addEventListener('cart:change', function (e) {
    var d = e.detail || {};
    if (d.ssGiftSync) return;                      // our own publish
    quietUntil = Date.now() + 1500;
    if (d.cart && d.cart.items) reconcile(d.cart, { baseEvent: d.baseEvent, at: Date.now() }); else schedule();
  });
  document.addEventListener('cart:refresh', schedule);

  /* 2. Backstops for cart changes made outside the theme (landing pages, Bundle Builder, other
     scripts). Shopify's web pixel loader re-assigns window.fetch after this file runs, so the
     fetch hook alone is unreliable; the PerformanceObserver sees every /cart/* request. Both
     stand down for 1.5s after an event-driven run, and wait 1s so the theme's own event wins so one change isn't reconciled three times. */
  var nativeFetch = window.fetch;
  window.fetch = function () {
    var url = arguments[0] && (arguments[0].url || arguments[0]);
    var p = nativeFetch.apply(this, arguments);
    try {
      if (typeof url === 'string' && /\/cart\/(add|change|update|clear)(\.js)?/.test(url)) {
        p.then(function () { setTimeout(scheduleFallback, FALLBACK_DELAY); }).catch(function () {});
      }
    } catch (e) {}
    return p;
  };
  try {
    new PerformanceObserver(function (list) {
      list.getEntries().forEach(function (e) {
        if (/\/cart\/(add|change|update|clear)(\.js)?(\?|$)/.test(e.name)) setTimeout(scheduleFallback, FALLBACK_DELAY);
      });
    }).observe({ type: 'resource', buffered: false });
  } catch (e) {}

  // 3. Coming back to the tab / bfcache restore — re-check the cart as it is now.
  window.addEventListener('pageshow', schedule);
  document.addEventListener('visibilitychange', function () { if (!document.hidden) schedule(); });

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { tiers(); reconcile(null, null); });
  else { tiers(); reconcile(null, null); }
})();
